import React from 'react'
import { useTranslation } from 'react-i18next'

import { FormikProps } from 'formik'
import { Card } from 'primereact/card'
import { CheckboxChangeEvent } from 'primereact/checkbox'
import { classNames } from 'primereact/utils'

import { PanelContainer } from '../../../../../components/Panel'
import {
  DropdownToggleable,
  MultiSelectToggleable,
} from '../../../../../components/inputs'
import { languageOptions } from '../../../../../utils/i18n'
import { EInvoicePartnerType, EditSupplierFormDto } from '../../data'
import { SupplierFormFieldName } from '../Supplier.data'

export enum DocumentTypeFlags {
  'None' = 0, // 0
  'XInvoice' = 1 << 0, // 1
  'ReadableInvoice' = 1 << 1, // 2
  'EmbeddedDocument' = 1 << 2, // 4
  'AdditionalDocument' = 1 << 3, // 8
  'FacturX' = 1 << 4, // 16
  'TransferProtocol' = 1 << 5, // 32
  'UpdatedReadableInvoice' = 1 << 6, // 64
  'CheckReport' = 1 << 7, // 128
}

type Props = {
  isEdit: boolean
  isRequestRunning: boolean
  isLoading: boolean
  formik: FormikProps<EditSupplierFormDto>
  isFormFieldInvalid: (name: SupplierFormFieldName) => boolean
  getFormErrorMessage: (name: SupplierFormFieldName) => JSX.Element
}

type DocumentTypeOption = {
  name: string
  code: DocumentTypeFlags
}

export const WebEdiEInvoiceSettings: React.FC<Props> = ({
  isEdit,
  isRequestRunning,
  isLoading,
  formik,
  isFormFieldInvalid,
  getFormErrorMessage,
}) => {
  const { t } = useTranslation('supplier')

  const documentTypeFlagsOptions: DocumentTypeOption[] = [
    {
      name: t('webEdiEInvoiceSettings.form.documentType.options.XInvoice'),
      code: DocumentTypeFlags.XInvoice,
    },
    {
      name: t(
        'webEdiEInvoiceSettings.form.documentType.options.ReadableInvoice',
      ),
      code: DocumentTypeFlags.ReadableInvoice,
    },
    {
      name: t(
        'webEdiEInvoiceSettings.form.documentType.options.EmbeddedDocument',
      ),
      code: DocumentTypeFlags.EmbeddedDocument,
    },
    {
      name: t(
        'webEdiEInvoiceSettings.form.documentType.options.AdditionalDocument',
      ),
      code: DocumentTypeFlags.AdditionalDocument,
    },
    {
      name: t('webEdiEInvoiceSettings.form.documentType.options.FacturX'),
      code: DocumentTypeFlags.FacturX,
    },
    {
      name: t(
        'webEdiEInvoiceSettings.form.documentType.options.TransferProtocol',
      ),
      code: DocumentTypeFlags.TransferProtocol,
    },
    {
      name: t(
        'webEdiEInvoiceSettings.form.documentType.options.UpdatedReadableInvoice',
      ),
      code: DocumentTypeFlags.UpdatedReadableInvoice,
    },
    {
      name: t('webEdiEInvoiceSettings.form.documentType.options.CheckReport'),
      code: DocumentTypeFlags.CheckReport,
    },
  ]

  const isProcessActiveOptions: { name: string; code: boolean }[] = [
    {
      name: t('webEdiEInvoiceSettings.form.activateEInvoice.options.active'),
      code: true,
    },
    {
      name: t(
        'webEdiEInvoiceSettings.form.activateEInvoice.options.deactivated',
      ),
      code: false,
    },
  ]

  const partnerTypeOptions: { name: string; code: EInvoicePartnerType }[] = [
    {
      name: t('webEdiEInvoiceSettings.form.partnerType.options.vendor'),
      code: EInvoicePartnerType.Vendor,
    },
    {
      name: t('webEdiEInvoiceSettings.form.partnerType.options.logicalSystem'),
      code: EInvoicePartnerType.LogicalSystem,
    },
  ]

  const getSelectedDocumentTypes = (): DocumentTypeFlags[] => {
    const bitFlags = formik.values.eInvoiceAttachmentDocumentTypeValue || 0
    return documentTypeFlagsOptions
      .map(option => option.code)
      .filter(flag => (bitFlags & flag) !== 0)
  }

  const handleDocumentTypesChange = (e: { value: DocumentTypeFlags[] }) => {
    const selectedFlags = e.value as DocumentTypeFlags[]
    let combinedFlags = 0
    selectedFlags.forEach(flag => {
      combinedFlags |= flag
    })
    formik.setFieldValue('eInvoiceAttachmentDocumentTypeValue', combinedFlags)
  }

  return (
    <PanelContainer
      header={t('webEdiEInvoiceSettings.title')}
      id="supplier-webEdiEInvoiceSettings"
      toggleable={false}
    >
      <div className="grid">
        <div className="col-12 lg:col-4">
          <p>{t('webEdiEInvoiceSettings.subtitle')}</p>
        </div>
        <div className="col-12 lg:col-7 lg:col-offset-1">
          <Card className="md:px-4">
            <div className="formgrid grid">
              {/* EInvoiceIsProcessActive */}
              <div className="col-12 md:col-6">
                <label
                  className="text-xs font-bold block mb-2"
                  htmlFor="eInvoiceIsProcessActiveValue"
                >
                  {t('webEdiEInvoiceSettings.form.activateEInvoice.label')}
                </label>

                <DropdownToggleable
                  checked={formik.values.eInvoiceIsProcessActiveOverride}
                  onCheckedChange={(e: CheckboxChangeEvent) => {
                    formik.setFieldValue(
                      'eInvoiceIsProcessActiveOverride',
                      e.checked,
                    )
                  }}
                  checkboxTooltip={t(
                    'webEdiEInvoiceSettings.form.activateEInvoice.checkboxTooltip',
                  )}
                  id="eInvoiceIsProcessActiveValue"
                  options={isProcessActiveOptions}
                  optionLabel="name"
                  optionValue="code"
                  value={formik.values.eInvoiceIsProcessActiveValue}
                  onChange={formik.handleChange}
                  className={classNames('w-full', {
                    'p-invalid': isFormFieldInvalid(
                      'eInvoiceIsProcessActiveValue',
                    ),
                  })}
                  disabled={!isEdit || isRequestRunning}
                />
              </div>

              {/* IsEInvoiceSupplierEnabled */}
              <div className="col-12 md:col-6">
                <label
                  className="text-xs font-bold block mb-2"
                  htmlFor="isEInvoiceSupplierEnabledValue"
                >
                  {t(
                    'webEdiEInvoiceSettings.form.isEInvoiceSupplierEnabled.label',
                  )}
                </label>

                <DropdownToggleable
                  checked={formik.values.isEInvoiceSupplierEnabledOverride}
                  onCheckedChange={(e: CheckboxChangeEvent) => {
                    formik.setFieldValue(
                      'isEInvoiceSupplierEnabledOverride',
                      e.checked,
                    )
                  }}
                  checkboxTooltip={t(
                    'webEdiEInvoiceSettings.form.isEInvoiceSupplierEnabled.checkboxTooltip',
                  )}
                  id="isEInvoiceSupplierEnabledValue"
                  options={isProcessActiveOptions}
                  optionLabel="name"
                  optionValue="code"
                  value={formik.values.isEInvoiceSupplierEnabledValue}
                  onChange={formik.handleChange}
                  className={classNames('w-full max-w-full', {
                    'p-invalid': isFormFieldInvalid(
                      'isEInvoiceSupplierEnabledValue',
                    ),
                  })}
                  disabled={!isEdit || isRequestRunning}
                />

                {getFormErrorMessage('isEInvoiceSupplierEnabledValue')}
              </div>

              {/* EInvoiceLanguage */}
              <div className="col-12">
                <label
                  className={classNames('text-xs font-bold block mb-2', {
                    'p-error': isFormFieldInvalid('eInvoiceLanguageValue'),
                  })}
                  htmlFor="eInvoiceLanguageValue"
                >
                  {t('webEdiEInvoiceSettings.form.language.label')}
                </label>
                <DropdownToggleable
                  checked={formik.values.eInvoiceLanguageOverride}
                  onCheckedChange={(e: CheckboxChangeEvent) => {
                    formik.setFieldValue('eInvoiceLanguageOverride', e.checked)
                  }}
                  checkboxTooltip={t(
                    'webEdiEInvoiceSettings.form.language.checkboxTooltip',
                  )}
                  id="eInvoiceLanguageValue"
                  options={languageOptions}
                  optionLabel="name"
                  optionValue="code"
                  value={formik.values.eInvoiceLanguageValue}
                  onChange={formik.handleChange}
                  className={classNames('w-full', {
                    'p-invalid': isFormFieldInvalid('eInvoiceLanguageValue'),
                  })}
                  disabled={!isEdit || isRequestRunning}
                />

                {getFormErrorMessage('eInvoiceLanguageValue')}
              </div>

              {/* Send document types */}
              <div className="col-12">
                <label
                  className={classNames('text-xs font-bold block mb-2', {
                    'p-error': isFormFieldInvalid(
                      'eInvoiceAttachmentDocumentTypeValue',
                    ),
                  })}
                  htmlFor="eInvoiceAttachmentDocumentTypeValue"
                >
                  {t('webEdiEInvoiceSettings.form.documentType.label')}
                </label>
                <MultiSelectToggleable
                  checked={formik.values.eInvoiceAttachmentDocumentTypeOverride}
                  onCheckedChange={(e: CheckboxChangeEvent) => {
                    formik.setFieldValue(
                      'eInvoiceAttachmentDocumentTypeOverride',
                      e.checked,
                    )
                  }}
                  checkboxTooltip={t(
                    'webEdiEInvoiceSettings.form.documentType.checkboxTooltip',
                  )}
                  id="eInvoiceAttachmentDocumentTypeValue"
                  options={documentTypeFlagsOptions}
                  optionLabel="name"
                  optionValue="code"
                  value={getSelectedDocumentTypes()}
                  onChange={handleDocumentTypesChange}
                  className={classNames('w-full', {
                    'p-invalid': isFormFieldInvalid(
                      'eInvoiceAttachmentDocumentTypeValue',
                    ),
                  })}
                  disabled={!isEdit || isRequestRunning}
                  display="chip"
                />

                {getFormErrorMessage('eInvoiceAttachmentDocumentTypeValue')}
              </div>

              {/* Partner type */}
              <div className="col-12">
                <label
                  className="text-xs font-bold block mb-2"
                  htmlFor="eInvoicePartnerTypeValue"
                >
                  {t('webEdiEInvoiceSettings.form.partnerType.label')}
                </label>

                <DropdownToggleable
                  checked={formik.values.eInvoicePartnerTypeOverride}
                  onCheckedChange={(e: CheckboxChangeEvent) => {
                    formik.setFieldValue(
                      'eInvoicePartnerTypeOverride',
                      e.checked,
                    )
                  }}
                  checkboxTooltip={t(
                    'webEdiEInvoiceSettings.form.partnerType.checkboxTooltip',
                  )}
                  id="eInvoicePartnerTypeValue"
                  options={partnerTypeOptions}
                  optionLabel="name"
                  optionValue="code"
                  value={formik.values.eInvoicePartnerTypeValue}
                  onChange={formik.handleChange}
                  className={classNames('w-full', {
                    'p-invalid': isFormFieldInvalid('eInvoicePartnerTypeValue'),
                  })}
                  disabled={!isEdit || isRequestRunning}
                />

                {getFormErrorMessage('eInvoicePartnerTypeValue')}
              </div>
            </div>
          </Card>
        </div>
      </div>
    </PanelContainer>
  )
}
